import React, { FC, ReactNode } from "react";
import { ScrollView, Text } from "react-native-web";
import { animated, useSpring } from "@react-spring/web";
import { Collapse } from "@mui/material";
import { vw } from "../../../utils/tool/vw";
import { ReactComponent as Hotel } from "../../../assets/icons/hotel.svg";
import { ReactComponent as Restaurant } from "../../../assets/icons/restaurant.svg";
import { ReactComponent as Phone } from "../../../assets/icons/phone-filled-black.svg";
import { ReactComponent as Distance } from "../../../assets/icons/distance.svg";
import { ReactComponent as Global } from "../../../assets/icons/global.svg";
import { ReactComponent as Arrow } from "../../../assets/icons/chevron.svg";
import { ReactComponent as ArrowTop } from "../../../assets/icons/chevron-top.svg";
import { ReactComponent as UberEasts } from "../../../assets/icons/uber-logo.svg";
import { ReactComponent as GrubNub } from "../../../assets/icons/grubhub.svg";
import { ReactComponent as Resy } from "../../../assets/icons/resy.svg";
import { ReactComponent as Yelp } from "../../../assets/icons/yelp.svg";
import { ReactComponent as Doordash } from "../../../assets/icons/doordash.svg";
import { ReactComponent as Sauce } from "../../../assets/icons/sauce.svg";
import { ReactComponent as OpenTable } from "../../../assets/icons/openTable.svg";
import { $gray, $midnightBlack } from "../../constants/style";
import styles from "./HeaderMenu.module.scss";
import { inlineStyles } from "./headerMenuInlineStyles";
import { localizeMenuName } from "../../../utils/localization/apply";
import { uiString } from "../../../utils/localization/uiStrings";
import { MenuType } from "../../types/ResponseType";
import { WeekDay } from "./useHeaderMenu";

interface HeaderMenuMobileHeroProps {
  menu: MenuType;
  locale: string;
  partners: any;
  openDays: WeekDay[];
  formatTime: (start: string, end: string, isWorkingDay: boolean | null) => string;
  status: string;
  openCloseTime: string;
  locationOpen: boolean;
  collapsedTime: boolean;
  setCollapsedTime: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
  toggleOpen: () => void;
  switcher: ReactNode;
  handleLinkClick: (url: string | null) => void;
  handlePhoneClick: (phone: string | null) => void;
  handleMapClick: (address: string) => void;
}

export const HeaderMenuMobileHero: FC<HeaderMenuMobileHeroProps> = ({
  menu,
  locale,
  partners,
  openDays,
  formatTime,
  status,
  openCloseTime,
  locationOpen,
  collapsedTime,
  setCollapsedTime,
  isOpen,
  toggleOpen,
  switcher,
  handleLinkClick,
  handlePhoneClick,
  handleMapClick,
}) => {
  const animatedStyle = useSpring({
    height: isOpen ? vw(600) : vw(80),
    overflow: "hidden",
  });

  return (
    <>
      <animated.div style={animatedStyle} className={styles.animatedContainer}>
        <div className={styles.restaurantCard}>
          <div className={styles.videoWrapper}>
            <video
              // @ts-ignore
              src={menu?.locationVideo[0].url}
              autoPlay
              muted
              loop
              className={styles.video}
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
          <div
            className={
              !collapsedTime ? styles.gradient : styles.gradientOpenTime
            }
          />
          <div className={styles.headerMenu}>
            <div className={styles.headerTitle}>
              {menu?.establishmentType === "restaurant" ? (
                <Restaurant width={20} height={20} />
              ) : (
                <Hotel width={20} height={20} />
              )}
              <Text style={inlineStyles.headerMenuTitle}>
                {localizeMenuName(menu, locale)}
              </Text>
              {switcher}
            </div>
            <div className={styles.locationInfoBlock}>
              <div
                onClick={() => setCollapsedTime((prev) => !prev)}
                style={{
                  marginBottom: 15,
                  zIndex: 15,
                  gap: vw(7),
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <div>
                  <Text style={inlineStyles.ItemText}>{openCloseTime}</Text>
                  <Text
                    style={[
                      inlineStyles.ItemText,
                      locationOpen
                        ? { color: "#95B35D" }
                        : { color: "#B35D5D" },
                    ]}
                  >
                    {" "}
                    ({status})
                  </Text>
                </div>
                {!collapsedTime ? (
                  <ArrowTop width={vw(16)} height={vw(16)} />
                ) : (
                  <Arrow width={vw(16)} height={vw(16)} />
                )}
              </div>
              <Collapse
                key={"workingHours"}
                in={collapsedTime}
                style={{ marginBottom: vw(10) }}
              >
                <div
                  style={{
                    flexDirection: "column",
                    display: "flex",
                    width: "68%",
                    gap: vw(5),
                  }}
                  onClick={() => setCollapsedTime(false)}
                >
                  {openDays.map((day) => (
                    <div className={styles.time}>
                      <div key={day.name}>{day.name}</div>
                      <div>
                        {formatTime(day.start, day.end, day.isWorkingDay)}
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse>
              <div>
                <ScrollView
                  contentContainerStyle={{
                    marginBottom: 24,
                    display: "flex",
                    flexDirection: "row",
                    gap: vw(16),
                  }}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                >
                  {partners?.UberEasts && (
                    <div
                      className={
                        partners?.UberEastsLink && styles.touchableOpacity
                      }
                      onClick={() => handleLinkClick(partners?.UberEastsLink)}
                    >
                      <UberEasts width={vw(61)} height={vw(10)} />
                    </div>
                  )}
                  {partners?.GrubNub && (
                    <div
                      className={
                        partners?.GrubNubLink && styles.touchableOpacity
                      }
                      onClick={() => handleLinkClick(partners?.GrubNubLink)}
                    >
                      <GrubNub width={vw(61)} height={vw(10)} />
                    </div>
                  )}
                  {partners?.Resy && (
                    <div
                      className={partners?.ResyLink && styles.touchableOpacity}
                      onClick={() => handleLinkClick(partners?.ResyLink)}
                    >
                      <Resy width={vw(61)} height={vw(10)} />
                    </div>
                  )}
                  {partners?.Yelp && (
                    <div
                      className={partners?.YelpLink && styles.touchableOpacity}
                      style={{
                        position: "relative",
                        height: vw(10),
                        width: vw(61),
                        zIndex: 29,
                      }}
                      onClick={() => handleLinkClick(partners?.YelpLink)}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: -5,
                          zIndex: 30,
                        }}
                      >
                        <Yelp width={vw(61)} height={vw(20)} />
                      </div>
                    </div>
                  )}
                  {partners?.Sauce && (
                    <div
                      className={partners?.SauceLink && styles.touchableOpacity}
                      onClick={() => handleLinkClick(partners?.SauceLink)}
                    >
                      <Sauce width={vw(61)} height={vw(10)} />
                    </div>
                  )}
                  {partners?.Doordash && (
                    <div
                      className={
                        partners?.DoordashLink && styles.touchableOpacity
                      }
                      onClick={() => handleLinkClick(partners?.DoordashLink)}
                    >
                      <Doordash width={vw(90)} height={vw(15)} />
                    </div>
                  )}
                  {partners?.openTable && (
                    <div
                      className={
                        partners?.openTableLink && styles.touchableOpacity
                      }
                      style={{
                        position: "relative",
                        height: vw(10),
                        width: vw(61),
                        zIndex: 29,
                      }}
                      onClick={() => handleLinkClick(partners?.openTableLink)}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          zIndex: 30,
                        }}
                      >
                        <OpenTable width={vw(90)} height={vw(15)} />
                      </div>
                    </div>
                  )}
                  <div />
                  <div />
                </ScrollView>
              </div>
              <div className={styles.communicationButtons}>
                <div
                  style={inlineStyles.ItemCall}
                  onClick={() => handlePhoneClick(menu.phoneNumber)}
                >
                  <Phone width={vw(14)} height={vw(14)} />
                  <Text
                    style={[inlineStyles.ItemText, { color: $midnightBlack }]}
                  >
                    {uiString(locale, "call")}
                  </Text>
                </div>
                <div
                  style={inlineStyles.communicationButton}
                  onClick={() => handleMapClick(menu.address)}
                >
                  <Distance width={vw(14)} height={vw(14)} />
                  <Text style={[inlineStyles.ItemText, { color: $gray }]}>
                    {uiString(locale, "directions")}
                  </Text>
                </div>
                {menu.website && (
                  <div
                    style={inlineStyles.communicationButton}
                    onClick={() => handleLinkClick(menu.website)}
                  >
                    <Global width={vw(14)} height={vw(14)} />
                    <Text style={[inlineStyles.ItemText, { color: $gray }]}>
                      {uiString(locale, "website")}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.buttonContainer} onClick={toggleOpen}>
          <div className={styles.btn}>
            <Text style={[inlineStyles.ItemText]}>
              {isOpen
                ? uiString(locale, "hideInfo")
                : uiString(locale, "readMoreHeader")}
            </Text>
            {isOpen ? (
              <ArrowTop width={vw(16)} height={vw(16)} />
            ) : (
              <Arrow width={vw(16)} height={vw(16)} />
            )}
          </div>
        </div>
      </animated.div>
      {!isOpen && <div className={`${styles.gradientBlack} ${styles.show}`} />}
    </>
  );
};
